import { useState, useCallback, useMemo, useEffect } from "react";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";
import { useQuery } from "@tanstack/react-query";


interface DoctorFilters {
    searchTerm: string;
    currentPage: number;
    doctorsPerPage: number;
}

const DEFAULT_FILTERS: DoctorFilters = {
    searchTerm: "",
    currentPage: 1,
    doctorsPerPage: 10,
};

export const useAdminDoctorList = () => {
    const [filters, setFilters] = useState<DoctorFilters>(DEFAULT_FILTERS);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(filters.searchTerm);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(filters.searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [filters.searchTerm]);

    const { data, isLoading: loading, error: queryError, refetch } = useQuery({
        queryKey: ['admin', 'doctors', { ...filters, searchTerm: debouncedSearchTerm }],
        queryFn: async () => {
            const response = await axiosInstance.get(API_ENDPOINTS.USERS.SEARCH, {
                params: {
                    role: "DOCTOR",
                    query: debouncedSearchTerm,
                    page: filters.currentPage - 1,
                    size: filters.doctorsPerPage,
                },
            });
            return response.data;
        },
    });

    const updateFilters = useCallback((newFilters: Partial<DoctorFilters>) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
            // Reset to page 1 if search changes
            currentPage: newFilters.searchTerm !== undefined ? 1 : (newFilters.currentPage ?? prev.currentPage)
        }));
    }, []);

    const doctors = useMemo(() => data?.content || [], [data]);
    const totalPages = data?.totalPages || 1;

    const error = queryError ? (queryError as any).message : null;

    return {
        doctors,
        loading,
        error,
        totalPages,
        filters,
        setFilters: updateFilters,
        refetch
    };
};
